import React, {useState} from 'react';

import {DownloadButton} from 'src/components/form-buttons';

import {Group} from '@pb/kit-component-group';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import Typography from '@pb/kit-component-typography';

import {ComparisonSection} from './comparison-section';
import {DEFAULT_PAGE_SIZE} from './constants';
import {buildSections} from './utils';

import type {UserSearchesReplaceResponse} from 'src/openapi/admin/models/UserSearchesReplaceResponse';

type Props = {
    migrationResult?: UserSearchesReplaceResponse | null,
    userName: string,
    accountName: string,
    hasTemplateUser: boolean,
    onDownload: () => void,
    downloadDisabled: boolean,
    templateUserName?: string,
};

export const MigrationResult = ({
    migrationResult,
    onDownload, downloadDisabled,
}: Props) => {
    const sections = migrationResult
        ? buildSections(migrationResult)
        : buildSections({} as UserSearchesReplaceResponse);

    const [{currentPage}, setCurrentPage] = useState<{currentPage: number}>(
        {currentPage: 1},
    );

    const totalRows = sections.reduce(
        (sum, s) => sum + Math.max(s.rows.length, 1),
        0,
    );

    const startIdx = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    const endIdx = Math.min(startIdx + DEFAULT_PAGE_SIZE, totalRows);

    let cumulativeIdx = 0;
    const sectionRanges = sections.map(s => {
        const size = Math.max(s.rows.length, 1);
        const range = {start: cumulativeIdx, end: cumulativeIdx + size};
        cumulativeIdx += size;
        return range;
    });

    return (
        <Group direction="v" style={{padding: 24, gap: 16}}>
            {sections.length > 0 && (
                <>
                    <Group justify="space-between">
                        <Typography fontWeight="semi-bold">Migration result</Typography>
                        <DownloadButton
                            disabled={downloadDisabled}
                            handler={onDownload}
                        />
                    </Group>
                    <Table fixed={false}>
                        <Head>
                            <Row>
                                <Cell as="th">
                                    <Typography fontWeight="semi-bold">
                                        Old Value
                                    </Typography>
                                </Cell>
                                <Cell as="th">
                                    <Typography fontWeight="semi-bold">
                                        New Value
                                    </Typography>
                                </Cell>
                            </Row>
                        </Head>
                        <Body>
                            {sections.map((section, idx) => {
                                const range = sectionRanges[idx];
                                const sectionStartIdx = Math.max(
                                    startIdx,
                                    range.start,
                                );
                                const sectionEndIdx = Math.min(
                                    endIdx,
                                    range.end,
                                );
                                return (
                                    <ComparisonSection
                                        key={section.key}
                                        title={section.title}
                                        rows={section.rows}
                                        startIdx={
                                            sectionStartIdx - range.start
                                        }
                                        endIdx={
                                            sectionEndIdx - range.start
                                        }
                                    />
                                );
                            })}
                        </Body>
                    </Table>
                    {totalRows > DEFAULT_PAGE_SIZE && (
                        <PaginationStripe
                            totalCount={totalRows}
                            currentPage={currentPage}
                            pageSize={DEFAULT_PAGE_SIZE}
                            showSizeSwitcher={false}
                            goToPage={true}
                            onPaginationChange={p => setCurrentPage({currentPage: p.currentPage})}
                        />
                    )}
                </>
            )}
        </Group>
    );
};
